<template>
    <div class="account-query-container">
        <!-- 查询条件区域 -->
        <el-card class="query-card" shadow="never">
            <template #header>
                <div class="card-header">
                    <span>创意查询</span>
                </div>
            </template>

            <el-form :model="queryParams" ref="queryForm" :inline="true" class="query-form">
                <el-form-item label="账户名称" prop="accountName">
                    <el-input type="textarea" v-model="queryParams.accountName" style="width: 200px; text-align: left;"
                        placeholder="每行一个账户名称" :rows="3">
                    </el-input>
                </el-form-item>

                <el-form-item label="区域" prop="area">
                    <el-select v-model="queryParams.area" placeholder="请选择区域" style="width: 150px">
                        <el-option v-for="item in regionOptions" :key="item.value" :label="item.label"
                            :value="item.value" />
                    </el-select>
                </el-form-item>

                <el-form-item label="时间范围" prop="timeRange">
                    <el-date-picker v-model="queryParams.timeRange" type="daterange" range-separator="至"
                        start-placeholder="开始日期" end-placeholder="结束日期" value-format="yyyy-MM-dd" format="yyyy-MM-dd"
                        style="width: 400px" />
                </el-form-item>

                <el-form-item>
                    <el-button type="primary" @click="handleQuery" icon="el-icon-search">
                        查询
                    </el-button>
                    <el-button @click="handleReset" icon="el-icon-refresh">
                        重置
                    </el-button>
                </el-form-item>
            </el-form>
        </el-card>

        <!-- 查询结果区域 -->
        <el-card class="result-card" shadow="never">
            <template #header>
                <div class="card-header">
                    <span>查询结果</span>
                    <div>
                        <el-button @click="handleRefresh" icon="el-icon-refresh">
                            刷新
                        </el-button>
                        <el-button type="primary" icon="el-icon-download" @click="handleExport">
                            导出Excel
                        </el-button>
                    </div>
                </div>
            </template>

            <el-table :data="tableData" v-loading="loading" border stripe style="width: 100%">
                <el-table-column prop="accountName" label="账户名称" min-width="120" show-overflow-tooltip />
                <el-table-column prop="id" label="创意ID" width="100" align="center" />
                <el-table-column prop="title" label="文案" min-width="200" show-overflow-tooltip />
                <el-table-column prop="createDate" label="创建日期" width="180" align="center" />
                <el-table-column prop="spent" label="花费" width="120" align="center">
                    <template slot-scope="scope">
                        <span v-if="scope.row.spent > 0">
                            ¥{{ scope.row.spent.toFixed(2) }}
                        </span>
                        <span v-else>¥0.00</span>
                    </template>
                </el-table-column>

                <el-table-column prop="statusDesc" label="状态" width="100" align="center">
                    <template slot-scope="scope">
                        <el-tag :type="getStatusType(scope.row.statusDesc)" size="small">
                            {{ scope.row.statusDesc }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="avatarFullUrl" label="头像素材" width="120" align="center">
                    <template slot-scope="scope">
                        <el-image v-if="scope.row.avatarFullUrl" :src="scope.row.avatarFullUrl"
                            :preview-src-list="[scope.row.avatarFullUrl]"
                            style="width: 40px; height: 40px; border-radius: 4px;">
                            <div slot="error" class="image-slot">
                                <i class="el-icon-picture-outline"></i>
                            </div>
                        </el-image>
                        <span v-else>-</span>
                    </template>
                </el-table-column>
                <el-table-column prop="imgsFullUrl" label="图片素材" width="120" align="center">
                    <template slot-scope="scope">
                        <el-image v-if="scope.row.imgsFullUrl" :src="scope.row.imgsFullUrl"
                            :preview-src-list="[scope.row.imgsFullUrl]"
                            style="width: 40px; height: 40px; border-radius: 4px;">
                            <div slot="error" class="image-slot">
                                <i class="el-icon-picture-outline"></i>
                            </div>
                        </el-image>
                        <span v-else>-</span>
                    </template>
                </el-table-column>
                <el-table-column prop="videoFullUrl" label="视频素材" width="120" align="center">
                    <template slot-scope="scope">
                        <div v-if="scope.row.videoFullUrl">
                            <el-button type="text" @click="handlePreviewVideo(scope.row.videoFullUrl)">
                                预览视频
                            </el-button>
                        </div>
                        <span v-else>-</span>
                    </template>
                </el-table-column>
                <el-table-column prop="campaignName" label="广告系列" min-width="150" show-overflow-tooltip />
                <el-table-column prop="groupName" label="广告组" min-width="150" show-overflow-tooltip />
                <el-table-column label="操作" width="120" fixed="right">
                    <template slot-scope="scope">
                        <el-button type="text" size="small" @click="handleView(scope.row)">
                            详情
                        </el-button>
                        <el-button type="text" size="small" @click="handleCopyUrl(scope.row)">
                            复制链接
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页组件 -->
            <el-pagination class="pagination" @size-change="handleSizeChange" @current-change="handleCurrentChange"
                :current-page="pagination.currentPage" :page-sizes="[10, 20, 50, 100]" :page-size="pagination.pageSize"
                layout="total, sizes, prev, pager, next, jumper" :total="pagination.total" />
        </el-card>

        <!-- 查看详情对话框 -->
        <el-dialog title="创意详情" :visible.sync="dialogVisible" width="800px">
            <el-descriptions :column="2" border v-if="currentRow">
                <el-descriptions-item label="账户名称">
                    {{ currentRow.accountName }}
                </el-descriptions-item>
                <el-descriptions-item label="创意ID">
                    {{ currentRow.id }}
                </el-descriptions-item>
                <el-descriptions-item label="创建日期">
                    {{ currentRow.createDate }}
                </el-descriptions-item>
                <el-descriptions-item label="状态">
                    <el-tag :type="getStatusType(currentRow.statusDesc)">
                        {{ currentRow.statusDesc }}
                    </el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="广告系列">
                    {{ currentRow.campaignName }}
                </el-descriptions-item>
                <el-descriptions-item label="广告组">
                    {{ currentRow.groupName }}
                </el-descriptions-item>
                <el-descriptions-item label="产品名称">
                    {{ currentRow.productName }}
                </el-descriptions-item>
                <el-descriptions-item label="文案" :span="2">
                    {{ currentRow.title }}
                </el-descriptions-item>
                <el-descriptions-item label="头像素材" :span="2">
                    <div v-if="currentRow.avatarFullUrl">
                        <el-image :src="currentRow.avatarFullUrl" style="max-width: 200px; max-height: 200px;" />
                        <div style="margin-top: 8px;">
                            <el-button type="text" @click="handleCopyText(currentRow.avatarFullUrl)">
                                复制链接
                            </el-button>
                        </div>
                    </div>
                    <span v-else>-</span>
                </el-descriptions-item>
                <el-descriptions-item label="图片素材" :span="2">
                    <div v-if="currentRow.imgsFullUrl">
                        <el-image :src="currentRow.imgsFullUrl" style="max-width: 200px; max-height: 200px;" />
                        <div style="margin-top: 8px;">
                            <el-button type="text" @click="handleCopyText(currentRow.imgsFullUrl)">
                                复制链接
                            </el-button>
                        </div>
                    </div>
                    <span v-else>-</span>
                </el-descriptions-item>
                <el-descriptions-item label="视频素材" :span="2">
                    <div v-if="currentRow.videoFullUrl">
                        <video :src="currentRow.videoFullUrl" controls style="max-width: 300px; max-height: 200px;"></video>
                        <div style="margin-top: 8px;">
                            <el-button type="text" @click="handleCopyText(currentRow.videoFullUrl)">
                                复制链接
                            </el-button>
                        </div>
                    </div>
                    <span v-else>-</span>
                </el-descriptions-item>
            </el-descriptions>
            <span slot="footer" class="dialog-footer">
                <el-button @click="dialogVisible = false">关闭</el-button>
            </span>
        </el-dialog>

        <!-- 视频预览对话框 -->
        <el-dialog title="视频预览" :visible.sync="videoDialogVisible" width="600px">
            <video v-if="currentVideoUrl" :src="currentVideoUrl" controls style="width: 100%; max-height: 400px;"></video>
            <span slot="footer" class="dialog-footer">
                <el-button @click="videoDialogVisible = false">关闭</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import axios from "axios";
export default {
    name: 'CreativeQuery',
    data() {
        return {
            regionOptions: [
                { label: "浙江", value: "1" },
                { label: "福建", value: "2" }
            ],
            // 查询参数
            queryParams: {
                accountName: '',
                area: '',
                timeRange: []
            },
            // 表格数据
            tableData: [],
            // 加载状态
            loading: false,
            // 分页参数
            pagination: {
                currentPage: 1,
                pageSize: 10,
                total: 0
            },
            // 对话框控制
            dialogVisible: false,
            videoDialogVisible: false,
            currentRow: null,
            currentVideoUrl: '',
            cookieList: [],
            AllCookieList: []
        }
    },

    methods: {

        async findIds() {
            if (!this.queryParams.accountName || this.queryParams.accountName.trim() === '') {
                this.$message.error('请输入账户名称');
                return;
            }

            if (!this.queryParams.area) {
                this.$message.error('请选择区域');
                return;
            }

            if (!this.queryParams.timeRange || this.queryParams.timeRange.length !== 2) {
                this.$message.error('请选择时间范围');
                return;
            }

            let [startTime, endTime] = this.queryParams.timeRange;
            let list = this.queryParams.accountName.split('\n')
                .filter(line => line.trim())
                .filter((item, index, array) => array.indexOf(item) === index);

            const requestData = {
                textList: list,
                area: this.queryParams.area,
                startDate: startTime,
                endDate: endTime
            };

            console.log('请求参数:', requestData);
            try {
                this.loading = true;
                const response = await this.$axios.post('https://control.zjwec.cn/api/vivo/tokenToCookie', requestData);
                if (response.data.code == '0' || response.data.success) {
                    const creativeData = response.data.data;
                    console.log('获取到的创意数据:', creativeData);
                    this.processCreativeData(creativeData);
                } else {
                    this.$message.error('接口返回错误: ' + response.data.message);
                }
            } catch (error) {
                console.error('请求失败:', error);
                this.$message.error('请求失败: ' + error.message);
            } finally {
                this.loading = false;
            }
        },
        processCreativeData(creativeData) {
            // 处理创意数据，提取所有创意列表
            const allCreatives = [];

            creativeData.creativeDataList.forEach(campaign => {
                if (campaign.data && campaign.data.list && campaign.data.list.length > 0) {
                    campaign.data.list.forEach(creative => {
                        // 为每个创意添加广告系列、广告组和账户名称信息
                        allCreatives.push({
                            ...creative,
                            campaignName: campaign.campaignName,
                            groupName: campaign.groupName,
                            accountName: creative.accountName || campaign.accountName || '未知账户' // 优先使用创意级别的账户名称
                        });
                    });
                }
            });

            // 按创建日期排序
            allCreatives.sort((a, b) => new Date(b.createDate) - new Date(a.createDate));

            this.tableData = allCreatives;
            this.pagination.total = allCreatives.length;
        },
        // 处理查询
        handleQuery() {
            if (this.queryParams.timeRange && this.queryParams.timeRange.length === 2) {
                const [startTime, endTime] = this.queryParams.timeRange
                if (new Date(startTime) > new Date(endTime)) {
                    this.$message.error('开始时间不能大于结束时间')
                    return
                }
            }

            this.pagination.currentPage = 1
            this.findIds()
        },
        // 处理重置
        handleReset() {
            this.queryParams = {
                accountName: '',
                area: '',
                timeRange: []
            }
            this.pagination.currentPage = 1
            this.tableData = []
        },
        // 处理刷新
        handleRefresh() {
            this.handleQuery()
        },
        // 获取数据
        async fetchData() {
            // 现在数据通过 findIds 方法获取
        },
        // 处理分页大小变化
        handleSizeChange(size) {
            this.pagination.pageSize = size
        },
        // 处理当前页变化
        handleCurrentChange(page) {
            this.pagination.currentPage = page
        },
        // 查看详情
        handleView(row) {
            this.currentRow = row
            this.dialogVisible = true
        },
        // 预览视频
        handlePreviewVideo(videoUrl) {
            this.currentVideoUrl = videoUrl
            this.videoDialogVisible = true
        },
        // 复制链接
        handleCopyUrl(row) {
            const textToCopy = `账户名称: ${row.accountName}\n创意ID: ${row.id}\n文案: ${row.title}\n创建日期: ${row.createDate}\n头像素材: ${row.avatarFullUrl || '无'}\n图片素材: ${row.imgsFullUrl || '无'}\n视频素材: ${row.videoFullUrl || '无'}`;
            this.handleCopyText(textToCopy);
        },
        // 复制文本
        handleCopyText(text) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            this.$message.success('已复制到剪贴板');
        },
        // 导出Excel
        handleExport() {
            if (this.tableData.length === 0) {
                this.$message.warning('没有数据可导出')
                return
            }

            // 导入xlsx库
            import('xlsx').then(XLSX => {
                // 准备Excel数据
                const excelData = this.tableData.map(row => ({
                    '账户名称': row.accountName || '',
                    '创意ID': row.id,
                    '文案': row.title,
                    '创建日期': row.createDate,
                    '花费': row.spent && row.spent > 0 ? `¥${row.spent.toFixed(2)}` : '0',
                    '状态': row.statusDesc,
                    '头像素材': row.avatarFullUrl || '',
                    '图片素材': row.imgsFullUrl || '',
                    '视频素材': row.videoFullUrl || '',
                    '广告系列': row.campaignName || '',
                    '广告组': row.groupName || '',
                    '产品名称': row.productName || ''
                }));

                // 创建工作簿
                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.json_to_sheet(excelData);

                // 设置列宽
                const colWidths = [
                    { wch: 15 }, // 账户名称
                    { wch: 10 }, // 创意ID
                    { wch: 30 }, // 文案
                    { wch: 18 }, // 创建日期
                    { wch: 12 }, // 花费
                    { wch: 10 }, // 状态
                    { wch: 40 }, // 头像素材
                    { wch: 40 }, // 图片素材
                    { wch: 40 }, // 视频素材
                    { wch: 25 }, // 广告系列
                    { wch: 25 }, // 广告组
                    { wch: 15 }  // 产品名称
                ];
                ws['!cols'] = colWidths;

                // 将工作表添加到工作簿
                XLSX.utils.book_append_sheet(wb, ws, '创意数据');

                // 生成Excel文件并下载
                const fileName = `创意数据_${new Date().toISOString().split('T')[0]}.xlsx`;
                XLSX.writeFile(wb, fileName);

                this.$message.success('Excel导出成功');
            }).catch(error => {
                console.error('导出Excel失败:', error);
                this.$message.error('导出Excel失败');
            });
        },
        // 获取状态标签类型
        getStatusType(status) {
            const statusMap = {
                '待审核': 'warning',
                '审核不通过': 'danger',
                '审核通过': 'success',
                '已上线': 'success',
                '已下线': 'info'
            }
            return statusMap[status] || 'info'
        }
    }
}
</script>

<style scoped>
.account-query-container {
    padding: 20px;
}

.query-card {
    margin-bottom: 20px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.query-form {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.pagination {
    margin-top: 20px;
    text-align: right;
}

.image-slot {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: #f5f7fa;
    color: #909399;
    font-size: 12px;
}
</style>


// WEBPACK FOOTER //
// src/components/vivo/creativeinfo.vue